import { matchPath } from 'react-router-dom'
import { LOAN_TYPES } from '@common/constants/client'
import { checkIsFinalized, FundingRequestStatus, resolveBBCLoanType } from '@common/interfaces/bbc'
import { IResponse, IFileDownloadResponse } from '@common/interfaces/request'
import { UserRole } from '@common/interfaces/user'
import { Action } from 'redux'
import { takeEvery, put, select } from 'redux-saga/effects'
import { ROUTES } from '../../constants/routes'
import { download } from '../../helpers/helpers'
import { putPromise } from '../../helpers/redux'
import { listCustomRules as listCustomEligibleRules } from '../clientInfo/actions'
import { clientInfo as client } from '../clientInfo/selectors'
import { calculateBBCFlags } from '../flag/actions'
import { role } from '../profile/selectors'
import {
  SUBMIT_BBC_FILES_SUCCESS,
  CREATE_EXTRA_RESERVES_SUCCESS,
  UPDATE_EXTRA_RESERVES_SUCCESS,
  CALCULATE_CLIENT_COLLATERAL_SUCCESS,
  EXPORT_BBC_SUCCESS,
  DOWNLOAD_TEMPLATE_SUCCESS,
  CREATE_BBC_RESERVE_SUCCESS,
  UPDATE_BBC_RESERVE_SUCCESS,
  DELETE_BBC_RESERVE_SUCCESS,
  DELETE_ALL_BBC_RESERVES_SUCCESS,
  CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
  UPDATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
  DELETE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
  EXPORT_BBC_AP_ROLLFORWARD_SUCCESS,
  EXPORT_BBC_AP_ROLLFORWARD_FAILURE,
  show,
  getBBCSummaryStats,
  calculateClientCollateral,
  listCustomRules,
  listCustomRulesLabels,
  CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_LABELS_SUCCESS,
  listARIneligibleStats,
  listInventoryIneligibleStatsSummary,
  EXPORT_BBC_AR_ROLLFORWARD_FAILURE,
  EXPORT_BBC_AR_ROLLFORWARD_SUCCESS,
  UPDATE_BBC_SUCCESS,
  getBBCFiles,
  DOWNLOAD_TEMPLATE_FAILURE,
  EXPORT_BBC_FAILURE,
} from './actions'
import { bbc } from './selectors'

export function* bbcUpdateSuccess() {
  yield takeEvery([UPDATE_BBC_SUCCESS], function* (data: IResponse<{ id: string }> & Action) {
    yield putPromise(show(data.data.id))
  })
}

export function* bbcFlagUpdate() {
  yield takeEvery([CALCULATE_CLIENT_COLLATERAL_SUCCESS], function* () {
    const userRole = (yield select(role)) as UserRole
    const pathMatches = matchPath({ path: `${ROUTES.BBC_SUMMARY}/*` }, window.location.pathname)
    if (userRole !== UserRole.PARTICIPANT_USER && pathMatches) {
      const current = (yield select(bbc)) as { id: string; status: FundingRequestStatus } | null
      if (current?.id && !checkIsFinalized(current.status)) {
        yield putPromise(calculateBBCFlags({ id: current.id }))
      }
    }
  })
}

export function* bbcFilesSubmitSuccess() {
  yield takeEvery([SUBMIT_BBC_FILES_SUCCESS], function* (data: IResponse<{ id: string }> & Action) {
    yield putPromise(show(data.data.id))
    yield putPromise(getBBCFiles(data.data.id))
  })
}

export function* refetchBBCReserves() {
  yield takeEvery(
    [
      CREATE_BBC_RESERVE_SUCCESS,
      UPDATE_BBC_RESERVE_SUCCESS,
      DELETE_BBC_RESERVE_SUCCESS,
      DELETE_ALL_BBC_RESERVES_SUCCESS,
    ],
    function* () {
      const current = (yield select(bbc)) as { id: string } | null
      if (current?.id) {
        yield putPromise(calculateClientCollateral(current.id))
      }
    },
  )
}

export function* recalculateClientCollateral() {
  yield takeEvery(
    [CREATE_EXTRA_RESERVES_SUCCESS, UPDATE_EXTRA_RESERVES_SUCCESS],
    function* (data: IResponse<{ id: string }> & Action) {
      yield putPromise(calculateClientCollateral(data.data.id))
    },
  )
}

export function* calculateClientCollateralSuccess() {
  yield takeEvery(
    [CALCULATE_CLIENT_COLLATERAL_SUCCESS],
    function* (data: IResponse<{ id: string }> & Action) {
      yield putPromise(show(data.data.id))
      yield putPromise(getBBCSummaryStats(data.data.id))

      // The summary renders every collateral section at once, so refresh each one it has.
      if (!matchPath({ path: ROUTES.BBC_SUMMARY, end: false }, window.location.pathname)) return
      const loanType = resolveBBCLoanType(yield select(bbc))
      if (loanType !== LOAN_TYPES.inventory) {
        yield putPromise(listARIneligibleStats(data.data.id))
      }
      if (loanType !== LOAN_TYPES.ar) {
        yield putPromise(listInventoryIneligibleStatsSummary(data.data.id))
      }
    },
  )
}

export function* downloadBBCExport() {
  yield takeEvery(EXPORT_BBC_SUCCESS, function* (action: IFileDownloadResponse & Action) {
    try {
      const payload = action.data
      yield download(payload, payload.name)
    } catch (error) {
      yield put({ type: EXPORT_BBC_FAILURE, error: error as Error })
    }
  })
}

export function* downloadTemplate() {
  yield takeEvery([DOWNLOAD_TEMPLATE_SUCCESS], function* (action: IFileDownloadResponse & Action) {
    try {
      const payload = action.data
      yield download(payload, payload.name)
    } catch (error) {
      yield put({ type: DOWNLOAD_TEMPLATE_FAILURE, error: error as Error })
    }
  })
}

export function* refetchCustomRules() {
  yield takeEvery(
    [
      CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
      UPDATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
      DELETE_INVENTORY_INELIGIBLE_CUSTOM_RULES_SUCCESS,
    ],
    function* () {
      const path = window.location.pathname

      if (matchPath({ path: ROUTES.CLIENT_SETUP }, path)) {
        const currentClient = (yield select(client)) as { id: string } | null
        if (currentClient?.id) {
          yield putPromise(listCustomEligibleRules({ id: currentClient.id }))
        }
      } else if (matchPath({ path: ROUTES.BBC_COLLATERAL_ELIGIBILITY, end: false }, path)) {
        const current = (yield select(bbc)) as { id: string } | null
        if (current?.id) {
          yield putPromise(listCustomRules({ borrowingBaseId: current.id, skipLoader: true }))
        }
      }
    },
  )
}

export function* refetchCustomRulesLabels() {
  yield takeEvery([CREATE_INVENTORY_INELIGIBLE_CUSTOM_RULES_LABELS_SUCCESS], function* () {
    yield putPromise(listCustomRulesLabels())
  })
}

export function* exportARRollforward() {
  yield takeEvery(
    EXPORT_BBC_AR_ROLLFORWARD_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        const fileName = action.data.name
        yield download(action.data, fileName)
      } catch (error) {
        yield put({ type: EXPORT_BBC_AR_ROLLFORWARD_FAILURE, error: error as Error })
      }
    },
  )
}

export function* exportAPRollforward() {
  yield takeEvery(
    EXPORT_BBC_AP_ROLLFORWARD_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        const fileName = action.data.name
        yield download(action.data, fileName)
      } catch (error) {
        yield put({ type: EXPORT_BBC_AP_ROLLFORWARD_FAILURE, error: error as Error })
      }
    },
  )
}
